import type { PreviewProps } from '@susu/custom-made/components/preview/preview';

import { createLayerUrlFromTemplate, createPreviewData } from '../imageUrlProvider/imageUrlProvider';
import type {
  Fabric,
  HiddenOptionsAndValues,
  LayerDefinitions,
  OptionsSelection,
  typeOptionValue,
} from '../interfaces';
import type { ConfigurationTypes, ProductType } from '../productDefinition';
import { getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId, Products } from '../productDefinition';
import { isExtraConfigurationInProduct, isObjectEmpty } from '../utils';

export interface LayerDefinition {
  src: string;
  srcset: string;
  srcSheet: string;
  srcRotation: string;
  srcsetRotation: string;
  sizes: string;
}

export const getProductPreviewLayers = (
  layerDefinitionsPerConfiguration: LayerDefinitions,
  optionValues: typeOptionValue,
  selectedFabric: Fabric,
  selectedOptions: OptionsSelection,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  product: ProductType,
  cdnDomain: string,
) => {
  const imgTemplates = new Map<string, PreviewProps>();
  if (!layerDefinitionsPerConfiguration || !optionValues || !selectedFabric || !selectedOptions) {
    return imgTemplates;
  }

  const tmpSelectedOptions: OptionsSelection = structuredClone(selectedOptions);

  for (const [configurationTypeId, configurationOptionSelection] of Object.entries(tmpSelectedOptions)) {
    Object.entries(configurationOptionSelection).forEach(([option, optionValue]) => {
      if (
        hiddenOptionsAndValues.optionsPerConfiguration[+configurationTypeId]?.includes(+option) ||
        hiddenOptionsAndValues.optionValuesPerConfiguration[+configurationTypeId]?.includes(optionValue)
      ) {
        delete tmpSelectedOptions[+configurationTypeId][+option];
      }
    });
  }

  const configurationTypeIds = Products[product].filter(id => !isExtraConfigurationInProduct(product, id));
  const previewData = configurationTypeIds.map(configurationTypeId => {
      const { fallbackUrl, layerDefinitions } = layerDefinitionsPerConfiguration.configurationTypes[+configurationTypeId];
      const processedImageTemplates = layerDefinitions.map(def =>
        createLayerUrlFromTemplate(def.imageTemplate, optionValues, tmpSelectedOptions, selectedFabric, configurationTypeId),
      );

      return createPreviewData(processedImageTemplates, fallbackUrl, cdnDomain);
  });

  configurationTypeIds.forEach((configurationTypeId, index) => {
    imgTemplates.set(configurationTypeId, previewData[index]);
  });

  return imgTemplates;
};

export const getExtraProductPreviewLayers = (
  layerDefinitionsPerConfiguration: LayerDefinitions,
  optionValues: typeOptionValue,
  selectedFabric: Fabric,
  selectedOptions: OptionsSelection,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  extraProductConfigurationId: ConfigurationTypes,
  cdnDomain: string,
) => {
  const imgTemplates = new Map<string, PreviewProps>();
  if (!layerDefinitionsPerConfiguration || !optionValues || !selectedFabric || !selectedOptions) {
    return imgTemplates;
  }

  const tmpSelectedOptions: OptionsSelection = structuredClone(selectedOptions);

  if (
    !tmpSelectedOptions[+extraProductConfigurationId] ||
    isObjectEmpty(tmpSelectedOptions[+extraProductConfigurationId])
  ) {
    return imgTemplates;
  }

  Object.entries(tmpSelectedOptions[+extraProductConfigurationId]).forEach(([option, optionValue]) => {
    if (
      hiddenOptionsAndValues.optionsPerConfiguration[+extraProductConfigurationId]?.includes(+option) ||
      hiddenOptionsAndValues.optionValuesPerConfiguration[+extraProductConfigurationId]?.includes(optionValue)
    ) {
      delete tmpSelectedOptions[+extraProductConfigurationId][+option];
    }
  });
  // To handle extra configurations which have different configuration type id but using definitions from base product
  const baseConfigurationTypeId =
    getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+extraProductConfigurationId);

  const { fallbackUrl, layerDefinitions } =
    layerDefinitionsPerConfiguration.configurationTypes[baseConfigurationTypeId];
  const processedImageTemplates = layerDefinitions.map(def => {
    return createLayerUrlFromTemplate(
      def.imageTemplate,
      optionValues,
      tmpSelectedOptions,
      selectedFabric,
      extraProductConfigurationId.toString() as ConfigurationTypes,
    );
  });

  const previewData = createPreviewData(processedImageTemplates, fallbackUrl, cdnDomain);
  imgTemplates.set(extraProductConfigurationId, previewData);

  return imgTemplates;
};
